<script setup>
import { ref, computed, onMounted } from 'vue'
import Main from './Main.vue'
import { Icon, Editable } from '@components'
import { sortedDays } from '@utils'

import { useCustomizer } from '@store/customizer'
const $customizer = useCustomizer()

import { useSettings } from '@store/settings'
const $settings = useSettings()

import moment from 'moment'
import { toRGB } from '../../../../utilities'

const pageDetails = ref({
    layoutTitle: 'Date',
    // frontendTitle: 'Select date and time'
})

const useCalendar = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const activeCalender = ref({
        month: 1, // Specify the month (1 for January, 2 for February, etc.)
        year: 0, // Specify the year
        dates: [],
    })

    onMounted(() => {
        activeCalender.value.month = new Date().getMonth() + 1
        activeCalender.value.year = new Date().getFullYear()
    })

    // generate month dates
    const generateMonthDates = () => {
        const firstDayOfMonth = moment(`${activeCalender.value.year}-${activeCalender.value.month}-01`, 'YYYY-MM-DD')
        const lastDayOfMonth = moment(firstDayOfMonth).endOf('month')

        const currentDate = moment(firstDayOfMonth)
        const startDayOfCalenderWeek = sortedDays()[0]
        const endDayOfCalenderWeek = sortedDays()[sortedDays().length - 1]

        activeCalender.value.dates = []

        // calculate the number of days to add at the beginning
        const daysToAddAtStart = (currentDate.day() + 7 - days.indexOf(startDayOfCalenderWeek)) % 7

        // add blank objects at the beginning
        let dateStartOfLastMonth = firstDayOfMonth.subtract(daysToAddAtStart, 'days')
        for (let i = 0; i < daysToAddAtStart; i++) {
            activeCalender.value.dates.push({
                day: dateStartOfLastMonth.format('dddd'),
                date: dateStartOfLastMonth.date(),
                month: dateStartOfLastMonth.format('MMMM'),
                year: dateStartOfLastMonth.format('YYYY'),
                fullDate: dateStartOfLastMonth.format('YYYY-MM-DD')
            })

            dateStartOfLastMonth.add(1, 'days')
        }

        while (currentDate.isSameOrBefore(lastDayOfMonth)) {
            activeCalender.value.dates.push({
                day: currentDate.format('dddd'),
                date: currentDate.date(),
                month: currentDate.format('MMMM'),
                year: currentDate.format('YYYY'),
                fullDate: currentDate.format('YYYY-MM-DD'),
            })

            currentDate.add(1, 'days')
        }

        // calculate the number of days to add at the end
        const daysToAddAtEnd = ((days.indexOf(endDayOfCalenderWeek) + 7 - currentDate.day()) % 7) + 1

        // add blank objects at the end
        if (daysToAddAtEnd > 6) return
        let dateEndOfLastMonth = lastDayOfMonth.add(1, 'days')
        for (let i = 0; i < daysToAddAtEnd; i++) {
            activeCalender.value.dates.push({
                day: dateEndOfLastMonth.format('dddd'),
                date: dateEndOfLastMonth.date(),
                month: dateEndOfLastMonth.format('MMMM'),
                year: dateEndOfLastMonth.format('YYYY'),
                fullDate: dateEndOfLastMonth.format('YYYY-MM-DD')
            })

            dateEndOfLastMonth.add(1, 'days')
        }
    }

    /**
     * Divide an array into groups of size
     * 
     * @param items - Array to be divided
     * @param groupSize - Size of each group
     * @returns - Array of groups
     * Example: divideArrayIntoGroups([1, 2, 3, 4, 5, 6, 7], 3) => [[1, 2, 3], [4, 5, 6], [7]]
     */
    const divideArrayIntoGroups = (items, groupSize) => {
        const resultArray = []
        for (let i = 0; i < items.length; i += groupSize) {
            resultArray.push(items.slice(i, i + groupSize))
        }
        return resultArray
    }

    // get dates for calender
    const getDates = computed(() => {
        generateMonthDates()
        return divideArrayIntoGroups(activeCalender.value.dates, 7)
    })

    // check if today
    const isToday = (fullDate) => {
        return moment().format('YYYY-MM-DD') === fullDate
    }

    // check if current month
    const isDisabled = (fullDate) => {
        // if not current month
        if (moment(`${activeCalender.value.year}-${activeCalender.value.month}`, 'YYYY-M').format('YYYY-MM') !== moment(fullDate, 'YYYY-MM-DD').format('YYYY-MM')) return true

        // if date is past from today
        if (moment(fullDate, 'YYYY-MM-DD').isBefore(moment().format('YYYY-MM-DD'))) return true

        // if date is off
        const dayName = moment(fullDate, 'YYYY-MM-DD').format('dddd'); // Gets day name like "Monday"
        if (!$settings.businessHours[dayName].enabled) return true;

        // if date is holiday
        return $settings.daysOff.holidays.some(holidayGroup => {
            if (holidayGroup.length === 1) {
                // Single date holiday
                return moment(holidayGroup[0].date, 'YYYY-MM-DD').isSame(moment(fullDate, 'YYYY-MM-DD'));
            } else if (holidayGroup.length === 2) {
                // Date range holiday
                const startDate = moment(holidayGroup[0].date, 'YYYY-MM-DD');
                const endDate = moment(holidayGroup[1].date, 'YYYY-MM-DD');
                const checkDate = moment(fullDate, 'YYYY-MM-DD');
                return checkDate.isBetween(startDate, endDate, 'day', '[]'); // [] means inclusive
            }
            return false;
        });
    }

    const changeMonth = (option) => {
        if (!option) return

        // on prev if month is current month then return
        if (option == 'prev' && moment().month(activeCalender.value.month - 1).format('MMMM') === moment().format('MMMM')) return
        console.log(option)
        // on next if month is future then return
        if (option == 'next' && moment().month(activeCalender.value.month - 1).format('MMMM') === moment().add(4, 'month').format('MMMM')) return
        const currentMonth = moment({
            year: activeCalender.value.year,
            month: activeCalender.value.month - 1, // Moment.js months are 0-based
        })

        // get the details of next or prev month
        const changeMonthTo = option == 'next' ? currentMonth.clone().add(1, 'month') : currentMonth.clone().subtract(1, 'month')

        activeCalender.value.month = changeMonthTo.month() + 1
        activeCalender.value.year = changeMonthTo.year()
        activeCalender.value.dates = []

        // getDates.value
        generateMonthDates()

    }


    return { getDates, isToday, isDisabled, activeCalender, changeMonth }
}
const { getDates, isToday, isDisabled, activeCalender, changeMonth } = useCalendar()

const progressHeight = () => {
    // bail, if progress is not enabled
    if (!$settings.customizer.date.show_progress) return
    else return  Math.round(Math.random() * 60)
}
</script>

<template>
    <Main class="prebook-customizer" :pageTitle="pageDetails.layoutTitle" @submit="$customizer.publish" :previous="{ name: $settings.addons.staffs ? 'customizer.form.staff' : 'customizer.form.service' }" :next="{ name: 'customizer.form.time' }">
        <Editable v-model="customizer.date.title" :iconVisibility="false" border inputClass="text-xl" :inputStyle="$customizer.titleStyle"></Editable>

        <div class="flex items-center justify-between gap-2 mt-5">
            <div class="text-[#344054] text-base font-semibold">{{ moment(`${activeCalender.year}-${activeCalender.month}`, 'YYYY-M').format('MMMM YYYY') }}</div>

            <div class="flex items-center gap-2" :class="{ 'flex-row-reverse': $settings.isRTL }">
                <Icon @click="changeMonth('prev')" name="chevronLeft" size="36px" class="cursor-pointer text-[#667085] p-2"></Icon>
                <Icon @click="changeMonth('next')" name="chevronRight" size="36px" class="cursor-pointer text-[#667085] p-2"></Icon>
            </div>
        </div>

        <hr class="my-2 border-[#EAECF0]">

        <div class="flex justify-between">
            <div v-for="day in sortedDays()" :key="day" class="flex justify-center items-center w-10 h-10 text-sm leading-5 font-medium text-[#667085]">{{ day.slice(0, 3) }}</div>
        </div>

        <!-- calendar dates -->
        <div>
            <div v-for="(row, index) in getDates" :key="index" class="flex justify-between gap-1">
                <div v-for="item in row" :key="item" class="overflow-hidden relative border text-base leading-6 font-semibold text-center w-10 h-10 text-[#344054] mb-[28.6px] border-[#dbcaef]" :class="{ '!text-white !border-2 !border-solid': isToday(item.fullDate) }, { '!text-[#98a2b3] !cursor-not-allowed !border-transparent': isDisabled(item.fullDate) }, { 'rounded': $customizer.isRounded }" :style="`background-color: ${isToday(item.fullDate) ? `${customizer.design.brand_color} !important` : 'transparent'}; border: 1px solid ${isToday(item.fullDate) ? `${customizer.design.brand_color}` : `${toRGB(customizer.design.brand_color, 0.3)}`};`">
                    <div v-if="!isToday(item.fullDate) && !isDisabled(item.fullDate)" class="absolute bottom-0 left-0 w-full transition-all" :style="`height:${progressHeight()}%; background-color: ${toRGB($settings.customizer.design.brand_color, 0.3)} !important;`"></div>
                    <div class="flex absolute justify-center items-center w-full h-full">{{ item.date }}</div>
                </div>
            </div>
        </div>
    </Main>
</template>
